<template>
  <v-dialog :value="value" scrollable width="910px" @input="$emit('input', $event)" @click:outside="persistChanges">
    <v-card class="pedidos-details">
      <v-card-title>
        <span class="block-title mr-6">Dados do pedido</span>
        <span class="status-box mr-6" :class="type">{{ capitalize(type) }}</span>
        <span class="status mr-3">Status atual: {{ info.status | status }}</span>
        <mf-button text size="sm" label="Imprimir" icon="print" @click="print" />
        <mf-button text size="sm" label="Exportar" icon="download" @click="exportCsv" />
        <v-spacer />
        <mf-icon class="close-icon" @click="closeDialog">close</mf-icon>
      </v-card-title>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row>
            <v-col cols="5" class="pb-0">
              <v-text-field
                v-model="info.agent"
                label="Responsável* "
                :disabled="info.status !== 'OPEN'"
                :error-messages="agentErrorMessage"
                class="mb-3"
                outlined
                autofocus
                @blur="persistChanges"
              />

              <v-container fluid class="pa-0">
                <v-row>
                  <v-col class="pt-0 pb-0" cols="7">
                    <date-picker
                      :disabled="info.status !== 'OPEN'"
                      :error-messages="dateErrorMessage"
                      :min="DateTime.now().toFormat('yyyy-MM-dd')"
                      :date.sync="info.date"
                      outlined
                      label="Data da retirada* "
                      @input="persistChanges"
                    />
                  </v-col>
                  <v-col class="pt-0 pb-0" cols="5">
                    <time-picker
                      :disabled="info.status !== 'OPEN' || !info.date"
                      :min="minTime"
                      :error-messages="timeErrorMessage"
                      :time.sync="info.time"
                      outline
                      label="Hora da retirada* "
                      @input="persistChanges"
                    />
                  </v-col>
                </v-row>
              </v-container>
              <v-text-field
                ref="money"
                v-model.lazy="purchaseValue"
                v-money="money"
                label="Valor final da compra"
                :disabled="info.status === 'AWAITING' || info.status === 'CANCELED' || info.status === 'CONCLUDED'"
                prefix="R$"
                class="mb-3"
                outlined
                @blur="persistChanges"
                @keydown="$event.key === '-' ? $event.preventDefault() : null"
              />
            </v-col>
            <v-col cols="7" class="pb-0">
              <v-textarea
                v-model="info.annotations"
                label="Anotações "
                :disabled="info.status === 'CONCLUDED' || info.status === 'CANCELED'"
                no-resize
                height="248"
                hide-details
                outlined
                @blur="persistChanges"
              />
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row>
            <v-col v-if="type === 'entrega'" class="pa-0">
              <label class="detail-title">Endereço de entrega</label>
              <p class="detail-address">
                {{ info.user.address | address }}
              </p>
            </v-col>
            <v-col v-else-if="type === 'retirada'" class="pa-0">
              <label class="detail-title">Endereço para retirada</label>
              <p class="detail-address">{{ info.unit.name }} - {{ info.unit.address }}</p>
            </v-col>
          </v-row>
          <v-row v-if="info.status !== 'CANCELED' && info.status !== 'CONCLUDED'">
            <mf-button label="Cancelar pedido" color="vermelho-d20" size="sm" :disabled="disableButton('')" @click="cancel" />
            <v-spacer />
            <div v-if="info.status === 'OPEN'">
              <mf-button
                label="Avançar para confirmado"
                size="sm"
                :loading="loading['CONFIRMED']"
                :disabled="disableButton('CONFIRMED')"
                @click="goToConfirmed"
              />
            </div>
            <div v-else-if="info.status === 'CONFIRMED'">
              <mf-button label="Voltar para aberto" size="sm" :loading="loading['OPEN']" :disabled="disableButton('OPEN')" @click="changeToStatus('OPEN')" />
              <mf-button
                class="ml-3"
                label="Avançar para separação"
                size="sm"
                :loading="loading['SEPARATION']"
                :disabled="disableButton('SEPARATION')"
                @click="changeToStatus('SEPARATION')"
              />
            </div>
            <div v-else-if="info.status === 'SEPARATION'">
              <mf-button
                label="Voltar para confirmado"
                size="sm"
                :loading="loading['CONFIRMED']"
                :disabled="disableButton('CONFIRMED')"
                @click="changeToStatus('CONFIRMED')"
              />
              <mf-button
                label="Avançar para preparado"
                class="ml-3"
                size="sm"
                :loading="loading['AWAITING']"
                :disabled="disableButton('AWAITING')"
                @click="changeToStatus('AWAITING')"
              />
            </div>
            <div v-else-if="info.status === 'AWAITING'">
              <mf-button
                label="Voltar para separação"
                size="sm"
                :loading="loading['SEPARATION']"
                :disabled="disableButton('SEPARATION')"
                @click="changeToStatus('SEPARATION')"
              />
              <mf-button
                label="Concluir pedido"
                class="ml-3"
                color="verde-0"
                size="sm"
                :loading="loading['CONCLUDED']"
                :disabled="disableButton('CONCLUDED')"
                @click="conclude"
              />
            </div>
          </v-row>
        </v-container>

        <v-divider class="my-4" />

        <v-container class="pa-0" fluid>
          <v-row>
            <v-col cols="4">
              <h3 class="block-title">Dados do cliente</h3>
            </v-col>
          </v-row>
          <v-row wrap>
            <v-col cols="4">
              <p class="detail-title">Nome do cliente</p>
              <p class="detail-value">{{ info.user.name }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">Data/hora do pedido</p>
              <p class="detail-value">{{ DateTime.fromISO(info.purchase.timestamp).toFormat('dd/MM/yyyy | HH:mm') }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">Forma de pagamento</p>
              <p class="detail-value">{{ info.purchase.payment.payment_method | paymentMethod }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">Número</p>
              <p class="detail-value">{{ info.user.phone | telefone }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">CPF na nota?</p>
              <p class="detail-value">{{ info.purchase.document_on_fiscal_note | yesOrNo }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">Valor da compra</p>
              <p class="detail-value">
                R$ {{ info.purchase.cart.total_value | localeString(2) }}
                <span>(+ R$ {{ info.purchase.rate | localeString(2) }} de taxa)</span>
              </p>
            </v-col>
            <v-col v-if="info.purchase.payment.payment_method === 'CASH'" cols="4">
              <p class="detail-title">Troco</p>
              <p class="detail-value">R$ {{ info.purchase.payment.exchange | localeString(2) }}</p>
            </v-col>
            <v-col cols="4">
              <p v-if="info.user.document_no.length === 11" class="detail-title">CPF</p>
              <p v-else class="detail-title">CNPJ</p>
              <p v-if="info.user.document_no.length === 11" class="detail-value">{{ info.user.document_no | cpf }}</p>
              <p v-else class="detail-value">{{ info.user.document_no | cnpj }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">Troca de produtos</p>
              <p class="detail-value">{{ info.purchase.missing_product_policy | missingProductPolicy }}</p>
            </v-col>
            <v-col cols="4">
              <p class="detail-title">Fidelidade?</p>
              <p class="detail-value">{{ info.loyalty | yesOrNo }}</p>
            </v-col>
            <v-col v-if="info.comment" cols="8">
              <p class="detail-title">Comentário</p>
              <p class="detail-value">{{ info.comment }}</p>
            </v-col>
          </v-row>
        </v-container>

        <v-divider class="my-6" />

        <h3 class="block-title">
          Lista de produto(s) ({{ info.purchase.cart.products.length }} {{ info.purchase.cart.products.length === 1 ? 'produto' : 'produtos' }})
        </h3>

        <print-layout v-show="false" id="print-layout" :info="info" />

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Nome</th>
                <th>EAN</th>
                <th>Quantidade</th>
                <th>Valor unitário</th>
                <th>Valor total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderBy(info.purchase.cart.products, 'name', 'asc')" :key="item._id">
                <td>{{ item.name }}</td>
                <td>{{ item.ean }}</td>
                <td>{{ item.type === 'WEIGHT' ? formatFloatToKg(item.quantity) : item.quantity }}</td>
                <td>R$ {{ item.unit_value | localeString(2) }}<span v-if="item.type === 'WEIGHT'">/Kg</span></td>
                <td>R$ {{ item.total_value | localeString(2) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import orderBy from 'lodash/orderBy'
import capitalize from 'lodash/capitalize'
import omit from 'lodash/omit'
import { required } from 'vuelidate/lib/validators'
import { notOnlySpace, timeIsFuture, isFuture } from '@/helpers/validators.js'
import { MUTATION_UPDATE_KANBAN_CARD_STATUS, MUTATION_UPDATE_KANBAN_CARD } from '../../graphql'
import { VMoney } from 'v-money'
import { formatFloatToKg, createCsvFromArray } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  components: {
    DatePicker: () => import('@/components/DatePicker.vue'),
    TimePicker: () => import('@/components/TimePicker.vue'),
    PrintLayout: () => import('@/modules/pedidos/controle-pedidos/views/pages/ControlePedidosPrintLayout.vue'),
  },
  directives: { money: VMoney },
  filters: {
    yesOrNo(value) {
      return value ? 'Sim' : 'Não'
    },
    paymentMethod(value) {
      switch (value) {
        case 'CASH':
          return 'Dinheiro'
        case 'DEBIT_CARD':
          return 'Cartão de débito'
        case 'CREDIT_CARD':
          return 'Cartão de crédito'
      }
    },
    missingProduct(value) {
      switch (value) {
        case 'CANCEL':
          return value
        case 'SWAP':
          return value
        case 'REMOVE':
          return value
      }
    },
    status(value) {
      switch (value) {
        case 'OPEN':
          return 'Aberto'
        case 'CONFIRMED':
          return 'Confirmado'
        case 'SEPARATION':
          return 'Separação'
        case 'AWAITING':
          return 'Preparado'
        case 'CONCLUDED':
          return 'Concluído'
        case 'CANCELED':
          return 'Cancelado'
      }
    },
    missingProductPolicy(value) {
      switch (value) {
        case 'CANCEL':
          return 'Cancele todo o pedido.'
        case 'SWAP':
          return 'Escolha o mesmo produto de outra marca.'
        case 'REMOVE':
          return 'Não escolha outro produdo.'
      }
    },
    address(value) {
      let address = ''

      if (value.postal_code) {
        const formatted_postal_code = value.postal_code.replace(/[^\d]/g, '').replace(/(\d{5})(\d{3})/, '$1-$2')
        address += formatted_postal_code + ', '
      }
      if (value.neighborhood) address += value.neighborhood + ', '
      if (value.street) address += value.street + ', '
      if (value.number) address += value.number + ', '
      if (value.complement) address += value.complement + ', '
      if (value.city_name) address += value.city_name + ', '
      if (value.federative_unit) address += value.federative_unit

      return address
    },
  },
  props: {
    value: Boolean,
    info: Object,
  },
  data: () => ({
    DateTime: DateTime,
    orderBy: orderBy,
    capitalize: capitalize,
    loading: {},
    money: {
      decimal: ',',
      thousands: '.',
      precision: 2,
      masked: false,
    },
    purchaseValue: null,
  }),
  computed: {
    type() {
      return this.info.purchase.delivery_method === 'DELIVERY' ? 'entrega' : 'retirada'
    },
    minTime() {
      if (DateTime.fromFormat(this.info.date, 'YYYY-MM-DD').toFormat('dd') === DateTime.now().toFormat('dd')) {
        return DateTime.now().toFormat('HH:mm')
      } else {
        return null
      }
    },
    agentErrorMessage() {
      if (this.$v.info.agent.$error) {
        if (!this.$v.info.agent.required) return 'Campo obrigatório.'
        else if (!this.$v.info.agent.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    dateErrorMessage() {
      if (this.$v.info.date.$error) {
        if (!this.$v.info.date.required) return 'Campo obrigatório.'
        else if (!this.$v.info.date.isFuture) return 'Data deve ser maior ou igual a atual.'
      }
      return ''
    },
    timeErrorMessage() {
      if (this.$v.info.time.$error) {
        if (!this.$v.info.agent.required) return 'Campo obrigatório.'
        else if (!this.$v.info.time.timeIsFuture) return 'Hora deve ser maior do que a atual.'
      }
      return ''
    },
  },
  mounted() {
    this.formatToVMoney(this.info.purchase_value)
  },
  methods: {
    formatFloatToKg,
    closeDialog() {
      this.$emit('input', false)
    },
    disableButton(status) {
      return Object.values(omit(this.loading, status)).includes(true)
    },
    print() {
      this.$htmlToPaper('print-layout')
    },
    exportCsv() {
      const arrayToExport = this.info.purchase.cart.products.map((obj) => {
        return {
          ean: obj.ean,
          nome: obj.name,
          quantidade: obj.quantity,
          valorUnitario: obj.unit_value,
          valorTotal: obj.total_value,
        }
      })
      const headers = ['Codigo Barras', 'Nome', 'Quantidade', 'Valor Unitário', 'Valor Total']
      createCsvFromArray(arrayToExport, `pedido_${this.info._id}`, headers)
    },
    goToConfirmed() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return
      }
      this.changeToStatus('CONFIRMED')
    },
    conclude() {
      this.$confirm({
        title: 'Concluir pedido',
        message: 'Tem certeza que deseja concluir esse pedido? Após concluído, ele irá para a página de histórico.',
        confirmText: 'Concluir',
        cancelText: 'Cancelar',
      })
        .then(async () => {
          await this.changeToStatus('CONCLUDED')
          this.closeDialog()
        })
        .catch(() => {})
    },
    cancel() {
      this.$confirm({
        title: 'Cancelar pedido?',
        message: 'Tem certeza que deseja cancelar esse pedido? Após cancelado, ele irá para a página de histórico.',
        confirmText: 'Cancelar',
        cancelText: 'Voltar',
      })
        .then(async () => {
          await this.changeToStatus('CANCELED')
          this.closeDialog()
        })
        .catch(() => {})
    },
    async persistChanges() {
      try {
        const { _id, agent, date, time, annotations } = this.info
        const { data } = await this.$apollo.mutate({
          mutation: MUTATION_UPDATE_KANBAN_CARD,
          variables: {
            data: {
              _id: _id,
              agent: agent,
              date: date,
              time: time,
              annotations: annotations,
              purchase_value: this.formatMoneyToFloat(this.purchaseValue),
            },
          },
        })

        this.$emit('update:info', data.updateKanbanCard)
      } catch {
        this.$snackbar({ message: 'Erro ao salvar alterações', snackbarColor: 'error' })
      }
    },
    async changeToStatus(status) {
      try {
        this.$set(this.loading, status, true)
        this.loading[status] = true

        await this.persistChanges()

        const { data } = await this.$apollo.mutate({
          mutation: MUTATION_UPDATE_KANBAN_CARD_STATUS,
          variables: {
            _id: this.info._id,
            status: status,
          },
        })

        this.$set(this.loading, status, false)
        this.$emit('update:info', data.updateKanbanCardStatus)
      } catch (error) {
        this.$snackbar({ message: 'Erro ao alterar status', snackbarColor: 'error' })
      }
    },
    formatMoneyToFloat(value) {
      if (!value) return null
      const floatValue = parseFloat(value.split('.').join('').replace(',', '.'))
      return floatValue > 0 ? floatValue : null
    },
    formatToVMoney(value) {
      if (value) {
        this.$refs.money.$el.getElementsByTagName('input')[0].value = value.toFixed(2)
      }
    },
  },
  validations: {
    info: {
      agent: { required, notOnlySpace },
      date: {
        required,
        isFuture,
      },
      time: {
        required,
        timeIsFuture: function (value) {
          return timeIsFuture(this.info.date, value)
        },
      },
    },
  },
}
</script>

<style lang="scss">
.pedidos-details {
  .block-title {
    opacity: 0.8;
    font-size: 18px;
    font-weight: bold;
    color: $preto;
  }
  .status {
    opacity: 0.5;
    font-size: 12px;
    line-height: 1.25;
    letter-spacing: 0.19px;
    color: $preto;
  }

  .status-box {
    padding: 4px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: bold;
    line-height: 1.25;
    letter-spacing: 0.19px;
    color: $branco;
    &.entrega {
      background-color: $marca-1;
    }
    &.retirada {
      background-color: $marca-0-l40;
    }
  }

  .close-icon {
    opacity: 0.5;
    cursor: pointer;
  }

  .detail-title {
    opacity: 0.8;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.2;
    letter-spacing: 0.24px;
    color: $preto;
    margin-bottom: 8px;
  }

  .detail-address {
    opacity: 0.8;
    font-size: 12px;
    line-height: 1.25;
    letter-spacing: 0.19px;
    text-align: left;
    color: $preto;
  }

  .detail-value {
    font-size: 12px;
    letter-spacing: 0.19px;
    color: $preto;
    margin-bottom: 12px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
}
</style>
